<script>
  import { tradeState } from '../../lib/stores/trade.svelte.js';
  import { binderState } from '../../lib/stores/binder.svelte.js';
  import {
    getCardsByStatus,
    updateCardStatus,
    removeCardFromTrade,
    updateCardQuantityInTrade,
    markCardExchanged,
    markAllCardsExchanged,
    unmarkAllCardsExchanged,
    getExchangeProgress,
    CARD_STATUS,
  } from '../../lib/services/trade-service.js';
  import { isFinalized, isComplete } from '../../lib/stores/trade.svelte.js';
  import { getCurrencySymbol } from '../../lib/logic/utils.js';
  import { dragresize } from '../../lib/actions/dragresize.js';
  import { listswipe } from '../../lib/actions/listswipe.js';
  import AcceptTradeButton from './AcceptTradeButton.svelte';
  import CancelTradeModal from './CancelTradeModal.svelte';
  import MoxfieldSyncModal from './MoxfieldSyncModal.svelte';
  import { exitTradeMode } from '../../lib/stores/trade.svelte.js';
  import { navigate } from '../../lib/stores/router.svelte.js';
  import { isMoxfieldBinder, getUserBinderUrl } from '../../lib/logic/moxfield-sync.js';

  // --- Local state ---
  let expanded = $state(false);
  let showCancelModal = $state(false);
  let showSyncModal = $state(false);
  let containerEl = $state(null);
  let overlayEl = $state(null);

  // --- Derived from trade state ---
  const trade = $derived(tradeState.currentTrade);
  const userSide = $derived(tradeState.userSide);
  const currencySymbol = $derived(getCurrencySymbol(binderState.selectedCurrency));

  // Get cards for each side
  const sideACards = $derived(trade?.sideA?.cards || []);
  const sideBCards = $derived(trade?.sideB?.cards || []);

  // User's side = cards the OTHER user selected from the user's binder
  // Other's side = cards the user selected from the other user's binder
  const yourCards = $derived(userSide === 'A' ? sideACards : sideBCards);
  const theirCards = $derived(userSide === 'A' ? sideBCards : sideACards);

  const yourGrouped = $derived(getCardsByStatus(yourCards));
  const theirGrouped = $derived(getCardsByStatus(theirCards));

  const hasCards = $derived(yourCards.length > 0 || theirCards.length > 0);

  // Finalized/complete state
  const finalized = $derived(isFinalized());
  const complete = $derived(isComplete());

  // Exchange progress — "theirs" = cards you receive, so you mark those
  const theirExchangeProgress = $derived(getExchangeProgress(theirCards));
  const yourExchangeProgress = $derived(getExchangeProgress(yourCards));

  // The Firestore side letter for "their" cards
  const theirFirestoreSide = $derived(userSide === 'A' ? 'B' : 'A');

  // Show Moxfield sync when finalized + user's binder is Moxfield
  const showMoxfieldSync = $derived.by(() => {
    if (!finalized || !trade) return false;
    const url = getUserBinderUrl(trade, userSide);
    return isMoxfieldBinder(url);
  });

  // Totals (trade-status cards only)
  function totalPrice(cards, currency) {
    return cards
      .filter((c) => (c.status || 'trade') === 'trade')
      .reduce((sum, c) => {
        const p = currency === 'eur' ? c.priceEur || 0 : c.priceUsd || 0;
        return sum + p * (c.quantity || 1);
      }, 0);
  }

  function cardPrice(card, currency) {
    const p = currency === 'eur' ? card.priceEur : card.priceUsd;
    return Number(p) || 0;
  }

  const yourTotal = $derived(totalPrice(yourCards, binderState.selectedCurrency));
  const theirTotal = $derived(totalPrice(theirCards, binderState.selectedCurrency));

  // Header prices: side A on left, side B on right (matching original layout)
  const headerPriceLeft = $derived(
    userSide === 'A'
      ? totalPrice(theirCards, binderState.selectedCurrency)
      : totalPrice(yourCards, binderState.selectedCurrency)
  );
  const headerPriceRight = $derived(
    userSide === 'A'
      ? totalPrice(yourCards, binderState.selectedCurrency)
      : totalPrice(theirCards, binderState.selectedCurrency)
  );

  // --- Drawer max height ---
  function updateMaxHeight() {
    if (!containerEl) return;
    const vh = window.innerHeight;
    const maxH = window.innerWidth <= 768 ? vh * 0.75 : vh * 0.5;
    containerEl.style.setProperty('--drawer-max-height', `${maxH}px`);
  }

  $effect(() => {
    if (containerEl) updateMaxHeight();
  });

  // --- Handlers ---
  function toggleDrawer() {
    expanded = !expanded;
  }

  function handleStateChange(isExpanded) {
    expanded = isExpanded;
  }

  function handleOverlayClick() {
    expanded = false;
  }

  async function handleRemoveCard(side, card) {
    const tradeSide = side === 'yours' ? userSide : userSide === 'A' ? 'B' : 'A';
    try {
      await removeCardFromTrade(tradeState.currentTradeId, tradeSide, {
        scryfallId: card.scryfallId,
        set: card.set,
        collectorNumber: card.collectorNumber,
        isFoil: card.isFoil,
      });
    } catch (err) {
      console.error('[TradeSplitDrawer] Failed to remove card:', err);
    }
  }

  async function handlePromoteCard(side, card) {
    const tradeSide = side === 'yours' ? userSide : userSide === 'A' ? 'B' : 'A';
    const currentStatus = card.status || CARD_STATUS.TRADE;
    let nextStatus;
    if (currentStatus === CARD_STATUS.CONSIDERING) nextStatus = CARD_STATUS.TRADE;
    else if (currentStatus === CARD_STATUS.SUGGESTED) nextStatus = CARD_STATUS.TRADE;
    else return;

    try {
      await updateCardStatus(
        tradeState.currentTradeId,
        tradeSide,
        {
          scryfallId: card.scryfallId,
          set: card.set,
          collectorNumber: card.collectorNumber,
          isFoil: card.isFoil,
        },
        nextStatus
      );
    } catch (err) {
      console.error('[TradeSplitDrawer] Failed to promote card:', err);
    }
  }

  async function handleDemoteCard(side, card) {
    const tradeSide = side === 'yours' ? userSide : userSide === 'A' ? 'B' : 'A';
    const currentStatus = card.status || CARD_STATUS.TRADE;
    let nextStatus;
    if (currentStatus === CARD_STATUS.TRADE) nextStatus = CARD_STATUS.CONSIDERING;
    else return;

    try {
      await updateCardStatus(
        tradeState.currentTradeId,
        tradeSide,
        {
          scryfallId: card.scryfallId,
          set: card.set,
          collectorNumber: card.collectorNumber,
          isFoil: card.isFoil,
        },
        nextStatus
      );
    } catch (err) {
      console.error('[TradeSplitDrawer] Failed to demote card:', err);
    }
  }

  async function handleQuantityChange(side, card, newQuantity) {
    const tradeSide = side === 'yours' ? userSide : userSide === 'A' ? 'B' : 'A';
    try {
      await updateCardQuantityInTrade(
        tradeState.currentTradeId,
        tradeSide,
        {
          scryfallId: card.scryfallId,
          name: card.name,
          set: card.set,
          setName: card.setName,
          collectorNumber: card.collectorNumber,
          isFoil: card.isFoil,
          priceUsd: card.priceUsd,
          priceEur: card.priceEur,
          status: card.status,
        },
        newQuantity
      );
    } catch (err) {
      console.error('[TradeSplitDrawer] Failed to update quantity:', err);
    }
  }

  function statusLabel(status) {
    if (status === 'trade') return 'In Trade';
    if (status === 'considering') return 'Considering';
    if (status === 'suggested') return 'Suggested';
    return status;
  }

  // --- Exchange handlers ---
  async function handleExchangeToggle(card) {
    const newQty = (card.exchangedQuantity || 0) > 0 ? 0 : 1;
    try {
      await markCardExchanged(
        tradeState.currentTradeId,
        theirFirestoreSide,
        {
          scryfallId: card.scryfallId,
          set: card.set,
          collectorNumber: card.collectorNumber,
          isFoil: card.isFoil,
        },
        newQty
      );
    } catch (err) {
      console.error('[TradeSplitDrawer] Exchange toggle failed:', err);
    }
  }

  async function handleExchangeQuantity(card, delta) {
    const current = card.exchangedQuantity || 0;
    const newQty = Math.max(0, Math.min(card.quantity || 1, current + delta));
    if (newQty === current) return;
    try {
      await markCardExchanged(
        tradeState.currentTradeId,
        theirFirestoreSide,
        {
          scryfallId: card.scryfallId,
          set: card.set,
          collectorNumber: card.collectorNumber,
          isFoil: card.isFoil,
        },
        newQty
      );
    } catch (err) {
      console.error('[TradeSplitDrawer] Exchange quantity failed:', err);
    }
  }

  async function handleMarkAllReceived() {
    try {
      const progress = theirExchangeProgress;
      if (progress.complete) {
        await unmarkAllCardsExchanged(tradeState.currentTradeId, theirFirestoreSide);
      } else {
        await markAllCardsExchanged(tradeState.currentTradeId, theirFirestoreSide);
      }
    } catch (err) {
      console.error('[TradeSplitDrawer] Mark all failed:', err);
    }
  }

  function isCardExchanged(card) {
    return (card.exchangedQuantity || 0) >= (card.quantity || 1);
  }

  function handleTradeCancelled() {
    showCancelModal = false;
    exitTradeMode();
    navigate('home');
  }

  // --- Swipe action helpers ---
  // Returns { onSwipeLeft, onSwipeRight, leftColor, rightColor } for a card in a given side/group.
  // null callbacks disable that direction.
  function getSwipeActions(side, group, card) {
    if (side === 'theirs') {
      // Their binder — you selected these cards, full control:
      //   trade:       left = demote (blue), right = none
      //   considering: left = remove (red),  right = promote (green)
      //   suggested:   left = remove (red),  right = promote (green)
      let onSwipeLeft;
      let leftColor;
      if (group === 'trade') {
        onSwipeLeft = () => handleDemoteCard('theirs', card);
        leftColor = '#3b82f6'; // blue for demote
      } else {
        onSwipeLeft = () => handleRemoveCard('theirs', card);
        leftColor = '#ef4444';
      }
      return {
        onSwipeRight: group !== 'trade' ? () => handlePromoteCard('theirs', card) : null,
        onSwipeLeft,
        rightColor: '#22c55e',
        leftColor,
      };
    } else {
      // Your binder — other user selected these, limited control:
      //   trade:       left = demote (blue), right = none
      //   considering: left = none,          right = promote (green)
      //   suggested:   left = remove (red),  right = none
      let onSwipeLeft = null;
      let leftColor = '#ef4444';
      if (group === 'trade') {
        onSwipeLeft = () => handleDemoteCard('yours', card);
        leftColor = '#3b82f6';
      } else if (group === 'suggested') {
        onSwipeLeft = () => handleRemoveCard('yours', card);
        leftColor = '#ef4444';
      }
      // considering: no left swipe (can only promote right)
      return {
        onSwipeRight: group === 'considering' ? () => handlePromoteCard('yours', card) : null,
        onSwipeLeft,
        rightColor: '#22c55e',
        leftColor,
      };
    }
  }
</script>

<svelte:window onresize={updateMaxHeight} />

<!-- Background overlay -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="drawer-overlay"
  class:is-visible={expanded && hasCards}
  bind:this={overlayEl}
  onclick={handleOverlayClick}
></div>

<!-- Drawer container -->
<div class="drawer-container" class:is-visible={hasCards} class:expanded bind:this={containerEl}>
  <!-- Trade split header: price left | "Trade" | price right -->
  <div
    class="trade-split-header"
    use:dragresize={{
      container: containerEl,
      overlay: overlayEl,
      onStateChange: handleStateChange,
      onTap: toggleDrawer,
    }}
  >
    <div class="trade-header-price">
      {currencySymbol}{headerPriceLeft.toFixed(2)}
    </div>
    <h2>Trade</h2>
    <div class="trade-header-price">
      {currencySymbol}{headerPriceRight.toFixed(2)}
    </div>
  </div>

  <!-- Trade split container -->
  <div class="trade-split-container">
    <!-- Side A (left) — always "theirs" visually -->
    <div class="trade-side">
      <div class="trade-side-header">
        <h3 class="trade-side-title">Theirs</h3>
        <div class="trade-side-price">
          {currencySymbol}{theirTotal.toFixed(2)}
        </div>
      </div>
      <div class="trade-side-content">
        {#if theirCards.length === 0}
          <div class="side-empty">No cards selected</div>
        {:else if finalized}
          <!-- Exchange mode: only trade cards, with exchange controls -->
          {#if theirGrouped.trade?.length > 0}
            <!-- Select All Received header -->
            {#if !complete}
              <!-- svelte-ignore a11y_click_events_have_key_events -->
              <!-- svelte-ignore a11y_no_static_element_interactions -->
              <div class="exchange-header" onclick={handleMarkAllReceived}>
                <span class="exchange-select-all-label">Select All Received</span>
                <span class="exchange-progress-badge">
                  {theirExchangeProgress.exchanged}/{theirExchangeProgress.total}
                </span>
                <input
                  type="checkbox"
                  class="exchange-checkbox"
                  checked={theirExchangeProgress.complete}
                  onchange={handleMarkAllReceived}
                  onclick={(e) => e.stopPropagation()}
                />
                <span class="exchange-header-price-spacer"></span>
              </div>
            {/if}
            <ul class="trade-group-list">
              {#each theirGrouped.trade as card (card.scryfallId + '_' + card.isFoil)}
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
                <li
                  class="exchange-row"
                  class:exchanged={isCardExchanged(card)}
                  onclick={() => {
                    if ((card.quantity || 1) === 1 && !complete) handleExchangeToggle(card);
                  }}
                >
                  <div class="card-details">
                    <div class="card-name-container">
                      <span class="card-name">
                        {card.name || 'Unknown'}
                        {#if card.isFoil}
                          <span class="foil-indicator"></span>
                        {/if}
                      </span>
                      <span class="card-set-info">
                        {card.setName || card.set?.toUpperCase() || ''}
                        {#if card.collectorNumber}
                          #{card.collectorNumber}
                        {/if}
                      </span>
                    </div>
                  </div>
                  <div class="exchange-controls">
                    {#if (card.quantity || 1) === 1}
                      <!-- Single qty: checkbox -->
                      <input
                        type="checkbox"
                        class="exchange-checkbox"
                        checked={(card.exchangedQuantity || 0) > 0}
                        disabled={complete}
                        onchange={() => handleExchangeToggle(card)}
                        onclick={(e) => e.stopPropagation()}
                        title={isCardExchanged(card) ? 'Mark as not received' : 'Mark as received'}
                      />
                    {:else}
                      <!-- Multi qty: -/count/+ -->
                      <button
                        class="exchange-qty-btn"
                        disabled={(card.exchangedQuantity || 0) <= 0 || complete}
                        onclick={(e) => {
                          e.stopPropagation();
                          handleExchangeQuantity(card, -1);
                        }}>−</button
                      >
                      <span class="exchange-qty-display">
                        {card.exchangedQuantity || 0}/{card.quantity}
                      </span>
                      <button
                        class="exchange-qty-btn"
                        disabled={(card.exchangedQuantity || 0) >= (card.quantity || 1) || complete}
                        onclick={(e) => {
                          e.stopPropagation();
                          handleExchangeQuantity(card, 1);
                        }}>+</button
                      >
                    {/if}
                  </div>
                  <div class="card-price">
                    {currencySymbol}{cardPrice(card, binderState.selectedCurrency).toFixed(2)}
                  </div>
                </li>
              {/each}
            </ul>
          {/if}
        {:else}
          <!-- Normal mode: swipe groups -->
          {#each ['trade', 'considering', 'suggested'] as group (group)}
            {#if theirGrouped[group]?.length > 0}
              <div class="trade-group">
                <div class="trade-group-header">
                  <span class="trade-group-icon status-{group}"></span>
                  <span class="trade-group-label">{statusLabel(group)}</span>
                  <span class="trade-group-count">{theirGrouped[group].length}</span>
                </div>
                <ul class="trade-group-list">
                  {#each theirGrouped[group] as card (card.scryfallId + '_' + card.isFoil)}
                    <li class="swipeable" use:listswipe={getSwipeActions('theirs', group, card)}>
                      <div
                        class="swipe-bg swipe-bg-left"
                        style:background-color={group === 'trade' ? '#3b82f6' : '#ef4444'}
                      >
                        <span class="swipe-icon">
                          {#if group === 'trade'}
                            <svg viewBox="0 0 24 24" fill="none"
                              ><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle
                                cx="12"
                                cy="12"
                                r="3"
                              ></circle></svg
                            >
                          {:else}
                            <svg viewBox="0 0 24 24" fill="none"
                              ><line x1="18" y1="6" x2="6" y2="18"></line><line
                                x1="6"
                                y1="6"
                                x2="18"
                                y2="18"
                              ></line></svg
                            >
                          {/if}
                        </span>
                      </div>
                      <div class="swipe-bg swipe-bg-right">
                        <span class="swipe-icon">
                          <svg viewBox="0 0 24 24" fill="none"
                            ><polyline points="20 6 9 17 4 12"></polyline></svg
                          >
                        </span>
                      </div>
                      <div class="swipe-content">
                        <div class="card-details">
                          <div class="card-name-container">
                            <span class="card-name">
                              {card.name || 'Unknown'}
                              {#if card.isFoil}
                                <span class="foil-indicator"></span>
                              {/if}
                            </span>
                            <span class="card-set-info">
                              {card.setName || card.set?.toUpperCase() || ''}
                              {#if card.collectorNumber}
                                #{card.collectorNumber}
                              {/if}
                            </span>
                          </div>
                        </div>
                        <div class="trade-action-buttons">
                          {#if group !== 'trade'}
                            <button
                              class="list-action-btn promote-btn"
                              title="Move to Trade"
                              onclick={() => handlePromoteCard('theirs', card)}
                            >
                              <svg viewBox="0 0 24 24" fill="none"
                                ><polyline points="20 6 9 17 4 12"></polyline></svg
                              >
                            </button>
                          {/if}
                          {#if group === 'trade'}
                            <button
                              class="list-action-btn demote-btn"
                              title="Move to Considering"
                              onclick={() => handleDemoteCard('theirs', card)}
                            >
                              <svg viewBox="0 0 24 24" fill="none"
                                ><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                ></path><circle cx="12" cy="12" r="3"></circle></svg
                              >
                            </button>
                          {/if}
                          <button
                            class="list-action-btn remove-btn"
                            title="Remove"
                            onclick={() => handleRemoveCard('theirs', card)}
                          >
                            <svg viewBox="0 0 24 24" fill="none"
                              ><line x1="18" y1="6" x2="6" y2="18"></line><line
                                x1="6"
                                y1="6"
                                x2="18"
                                y2="18"
                              ></line></svg
                            >
                          </button>
                        </div>
                        <div class="list-quantity-controls">
                          {#if (card.availableQuantity || 1) > 1}
                            <select
                              class="list-quantity-select"
                              value={card.quantity || 1}
                              onchange={(e) =>
                                handleQuantityChange(
                                  'theirs',
                                  card,
                                  parseInt(e.currentTarget.value)
                                )}
                              onclick={(e) => e.stopPropagation()}
                            >
                              {#each Array(card.availableQuantity || 1) as _, i (i)}
                                <option value={i + 1}>{i + 1}</option>
                              {/each}
                            </select>
                          {:else}
                            &nbsp;
                          {/if}
                        </div>
                        <div class="card-price">
                          {currencySymbol}{cardPrice(card, binderState.selectedCurrency).toFixed(2)}
                        </div>
                      </div>
                    </li>
                  {/each}
                </ul>
              </div>
            {/if}
          {/each}
        {/if}
      </div>
    </div>

    <!-- Divider -->
    <div class="trade-divider"></div>

    <!-- Side B (right) — always "yours" visually -->
    <div class="trade-side">
      <div class="trade-side-header">
        <h3 class="trade-side-title">Yours</h3>
        <div class="trade-side-price">
          {currencySymbol}{yourTotal.toFixed(2)}
        </div>
      </div>
      <div class="trade-side-content">
        {#if yourCards.length === 0}
          <div class="side-empty">No cards selected</div>
        {:else if finalized}
          <!-- Exchange mode: read-only view of your cards (other user marks these) -->
          {#if yourGrouped.trade?.length > 0}
            <div class="exchange-header exchange-header-readonly">
              <span class="exchange-header-label">Exchange Progress</span>
              <span class="exchange-progress-badge">
                {yourExchangeProgress.exchanged}/{yourExchangeProgress.total} Received
              </span>
            </div>
            <ul class="trade-group-list">
              {#each yourGrouped.trade as card (card.scryfallId + '_' + card.isFoil)}
                <li class="exchange-row" class:exchanged={isCardExchanged(card)}>
                  <div class="card-details">
                    <div class="card-name-container">
                      <span class="card-name">
                        {card.name || 'Unknown'}
                        {#if card.isFoil}
                          <span class="foil-indicator"></span>
                        {/if}
                      </span>
                      <span class="card-set-info">
                        {card.setName || card.set?.toUpperCase() || ''}
                        {#if card.collectorNumber}
                          #{card.collectorNumber}
                        {/if}
                      </span>
                    </div>
                  </div>
                  <div class="exchange-controls">
                    {#if (card.quantity || 1) > 1}
                      <span class="exchange-qty-display">
                        {card.exchangedQuantity || 0}/{card.quantity}
                      </span>
                    {:else}
                      <input
                        type="checkbox"
                        class="exchange-checkbox"
                        checked={(card.exchangedQuantity || 0) > 0}
                        disabled
                      />
                    {/if}
                  </div>
                  <div class="card-price">
                    {currencySymbol}{cardPrice(card, binderState.selectedCurrency).toFixed(2)}
                  </div>
                </li>
              {/each}
            </ul>
          {/if}
        {:else}
          <!-- Normal mode: swipe groups -->
          {#each ['trade', 'considering', 'suggested'] as group (group)}
            {#if yourGrouped[group]?.length > 0}
              <div class="trade-group">
                <div class="trade-group-header">
                  <span class="trade-group-icon status-{group}"></span>
                  <span class="trade-group-label">{statusLabel(group)}</span>
                  <span class="trade-group-count">{yourGrouped[group].length}</span>
                </div>
                <ul class="trade-group-list">
                  {#each yourGrouped[group] as card (card.scryfallId + '_' + card.isFoil)}
                    <li class="swipeable" use:listswipe={getSwipeActions('yours', group, card)}>
                      <div
                        class="swipe-bg swipe-bg-left"
                        style:background-color={group === 'trade' ? '#3b82f6' : '#ef4444'}
                      >
                        <span class="swipe-icon">
                          {#if group === 'trade'}
                            <svg viewBox="0 0 24 24" fill="none"
                              ><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle
                                cx="12"
                                cy="12"
                                r="3"
                              ></circle></svg
                            >
                          {:else}
                            <svg viewBox="0 0 24 24" fill="none"
                              ><line x1="18" y1="6" x2="6" y2="18"></line><line
                                x1="6"
                                y1="6"
                                x2="18"
                                y2="18"
                              ></line></svg
                            >
                          {/if}
                        </span>
                      </div>
                      <div class="swipe-bg swipe-bg-right">
                        <span class="swipe-icon">
                          <svg viewBox="0 0 24 24" fill="none"
                            ><polyline points="20 6 9 17 4 12"></polyline></svg
                          >
                        </span>
                      </div>
                      <div class="swipe-content">
                        <div class="card-details">
                          <div class="card-name-container">
                            <span class="card-name">
                              {card.name || 'Unknown'}
                              {#if card.isFoil}
                                <span class="foil-indicator"></span>
                              {/if}
                            </span>
                            <span class="card-set-info">
                              {card.setName || card.set?.toUpperCase() || ''}
                              {#if card.collectorNumber}
                                #{card.collectorNumber}
                              {/if}
                            </span>
                          </div>
                        </div>
                        <div class="trade-action-buttons">
                          {#if group === 'considering'}
                            <button
                              class="list-action-btn promote-btn"
                              title="Move to Trade"
                              onclick={() => handlePromoteCard('yours', card)}
                            >
                              <svg viewBox="0 0 24 24" fill="none"
                                ><polyline points="20 6 9 17 4 12"></polyline></svg
                              >
                            </button>
                          {/if}
                          {#if group === 'trade'}
                            <button
                              class="list-action-btn demote-btn"
                              title="Move to Considering"
                              onclick={() => handleDemoteCard('yours', card)}
                            >
                              <svg viewBox="0 0 24 24" fill="none"
                                ><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                ></path><circle cx="12" cy="12" r="3"></circle></svg
                              >
                            </button>
                          {/if}
                          <button
                            class="list-action-btn remove-btn"
                            title="Remove"
                            onclick={() => handleRemoveCard('yours', card)}
                          >
                            <svg viewBox="0 0 24 24" fill="none"
                              ><line x1="18" y1="6" x2="6" y2="18"></line><line
                                x1="6"
                                y1="6"
                                x2="18"
                                y2="18"
                              ></line></svg
                            >
                          </button>
                        </div>
                        <div class="list-quantity-controls">
                          {#if (card.availableQuantity || 1) > 1}
                            <select
                              class="list-quantity-select"
                              value={card.quantity || 1}
                              onchange={(e) =>
                                handleQuantityChange(
                                  'yours',
                                  card,
                                  parseInt(e.currentTarget.value)
                                )}
                              onclick={(e) => e.stopPropagation()}
                            >
                              {#each Array(card.availableQuantity || 1) as _, i (i)}
                                <option value={i + 1}>{i + 1}</option>
                              {/each}
                            </select>
                          {:else}
                            &nbsp;
                          {/if}
                        </div>
                        <div class="card-price">
                          {currencySymbol}{cardPrice(card, binderState.selectedCurrency).toFixed(2)}
                        </div>
                      </div>
                    </li>
                  {/each}
                </ul>
              </div>
            {/if}
          {/each}
        {/if}
      </div>
    </div>
  </div>

  <!-- Accept / Cancel footer -->
  <div class="accept-footer">
    {#if showMoxfieldSync}
      <button class="sync-moxfield-btn" onclick={() => (showSyncModal = true)}>
        Sync to Moxfield
      </button>
    {/if}
    <AcceptTradeButton onCancel={() => (showCancelModal = true)} />
  </div>
</div>

<MoxfieldSyncModal bind:isOpen={showSyncModal} {trade} {userSide} />

<CancelTradeModal
  isOpen={showCancelModal}
  tradeId={tradeState.currentTradeId}
  onClose={() => (showCancelModal = false)}
  onCancelled={handleTradeCancelled}
/>

<style>
  /* Overlay */
  .drawer-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 49;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.3s ease-in-out,
      visibility 0s linear 0.3s;
    pointer-events: none;
  }
  .drawer-overlay.is-visible {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition-delay: 0s, 0s;
  }

  /* Drawer container */
  .drawer-container {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background-color: #fff;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
    height: 55px;
    max-width: 100%;
    margin: 0 auto;
    overflow: hidden;
    translate: 0 100%;
    visibility: hidden;
    transition:
      translate 0.3s ease-in-out,
      height 0.3s ease-in-out,
      visibility 0.3s;
    transition-behavior: allow-discrete;
    display: flex;
    flex-direction: column;
  }
  .drawer-container.is-visible {
    translate: 0 0;
    visibility: visible;
  }
  @starting-style {
    .drawer-container.is-visible {
      translate: 0 100%;
    }
  }
  .drawer-container.expanded {
    height: var(--drawer-max-height, 50vh);
  }
  @media (max-width: 768px) {
    .drawer-container.expanded {
      height: var(--drawer-max-height, 75vh);
    }
  }
  .drawer-container :global(.dragging) {
    transition: none;
  }

  /* ========================================
     TRADE SPLIT HEADER — matches original styles.css:2770
     ======================================== */
  .trade-split-header {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    height: 55px;
    cursor: grab;
    touch-action: none;
    user-select: none;
    box-sizing: border-box;
    flex-shrink: 0;
  }
  .trade-split-header:active {
    cursor: grabbing;
  }
  .trade-split-header::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 4px;
    background: #d1d5db;
    border-radius: 2px;
  }
  @media (pointer: fine) {
    .trade-split-header::before {
      display: none;
    }
  }
  .trade-split-header h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
  }
  .trade-header-price {
    font-size: 0.875rem;
    font-weight: 600;
    color: #1f2937;
    min-width: 60px;
  }
  .trade-header-price:last-child {
    text-align: right;
  }

  /* ========================================
     TRADE SPLIT CONTAINER — matches original styles.css:2819
     ======================================== */
  .trade-split-container {
    display: flex;
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }

  .trade-side {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
  }

  .trade-side-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    background-color: #f9fafb;
    flex-shrink: 0;
  }

  .trade-side-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
  }

  .trade-side-price {
    font-size: 0.875rem;
    font-weight: 600;
    color: #1f2937;
  }

  .trade-side-content {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .side-empty {
    text-align: center;
    color: #9ca3af;
    font-size: 0.8rem;
    padding: 1.5rem 0.5rem;
  }

  .trade-divider {
    width: 2px;
    background: linear-gradient(to bottom, #e5e7eb 0%, #9ca3af 50%, #e5e7eb 100%);
    flex-shrink: 0;
  }

  /* ========================================
     TRADE GROUPS — matches original styles.css:3074
     ======================================== */
  .trade-group {
    border-bottom: 1px solid #e5e7eb;
  }
  .trade-group:last-child {
    border-bottom: none;
  }

  .trade-group-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background-color: #f9fafb;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  /* 16px circle icons with SVG/text — matches original styles.css:3096 */
  .trade-group-icon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .trade-group-icon.status-trade {
    background-color: #22c55e;
  }
  .trade-group-icon.status-trade::before {
    content: '';
    width: 10px;
    height: 10px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
  }
  .trade-group-icon.status-considering {
    background-color: #3b82f6;
  }
  .trade-group-icon.status-considering::before {
    content: '';
    width: 10px;
    height: 10px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'%3E%3C/path%3E%3Ccircle cx='12' cy='12' r='3'%3E%3C/circle%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
  }
  .trade-group-icon.status-suggested {
    background-color: #f59e0b;
  }
  .trade-group-icon.status-suggested::before {
    content: '?';
    color: white;
    font-weight: 700;
    font-size: 10px;
    line-height: 1;
  }

  .trade-group-label {
    flex: 1;
  }

  .trade-group-count {
    background-color: #e5e7eb;
    color: #374151;
    padding: 0.125rem 0.375rem;
    border-radius: 9999px;
    font-size: 0.625rem;
    min-width: 1.25rem;
    text-align: center;
  }

  /* ========================================
     TRADE GROUP LIST ITEMS — matches original styles.css:3249
     ======================================== */
  .trade-group-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .trade-group-list li {
    position: relative;
    overflow: hidden;
    min-height: 44px;
    font-size: 0.75rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.4rem 0.75rem;
    background-color: white;
    border-bottom: 1px solid #f3f4f6;
    gap: 0.25rem;
  }
  .trade-group-list li:last-child {
    border-bottom: none;
  }

  /* Card details — matches original styles.css:1993 */
  .card-details {
    display: flex;
    align-items: center;
    flex-grow: 1;
    margin-right: 0.25rem;
    overflow: hidden;
  }

  .card-name-container {
    flex-grow: 1;
    margin-right: 0.5rem;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .card-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-set-info {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75em;
    color: #6b7280;
  }

  .foil-indicator {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--foil-gradient);
    margin-left: 0.2rem;
    vertical-align: middle;
    transform: translateY(-0.05em);
    flex-shrink: 0;
  }

  .card-price {
    font-weight: 500;
    min-width: 45px;
    text-align: right;
    flex-shrink: 0;
  }

  .list-quantity-controls {
    display: flex;
    align-items: center;
    min-width: 45px;
    justify-content: center;
    margin-right: 0.25rem;
    flex-shrink: 0;
  }

  .list-quantity-select {
    padding: 2px 4px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.75rem;
    background-color: white;
    cursor: pointer;
    min-width: 45px;
  }

  .list-quantity-select:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  /* ========================================
     ACTION BUTTONS — matches original styles.css:3367
     Hidden by default, shown on desktop hover
     ======================================== */
  .trade-action-buttons {
    display: none;
    flex-direction: row;
    align-items: center;
    flex-shrink: 0;
    gap: 2px;
  }

  @media (hover: hover) and (pointer: fine) {
    .trade-group-list li:hover .trade-action-buttons,
    .trade-group-list li .swipe-content:hover .trade-action-buttons {
      display: flex;
    }
  }

  .list-action-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background-color: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s;
  }

  .list-action-btn svg {
    width: 16px;
    height: 16px;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
  }

  .list-action-btn.promote-btn svg {
    stroke: #22c55e;
  }
  .list-action-btn.promote-btn:not(:disabled):hover {
    background-color: rgba(34, 197, 94, 0.15);
  }

  .list-action-btn.demote-btn svg {
    stroke: #3b82f6;
  }
  .list-action-btn.demote-btn:not(:disabled):hover {
    background-color: rgba(59, 130, 246, 0.15);
  }

  .list-action-btn.remove-btn svg {
    stroke: #ef4444;
  }
  .list-action-btn.remove-btn:not(:disabled):hover {
    background-color: rgba(239, 68, 68, 0.15);
  }

  /* ========================================
     MOBILE RESPONSIVE — matches original styles.css:2856
     ======================================== */
  @media (max-width: 768px) {
    .trade-group-list li,
    .trade-group-list li .swipe-content {
      flex-wrap: wrap;
      gap: 0.125rem;
    }
    /* Exchange rows stay single-line (no action buttons/qty selectors to wrap) */
    .trade-group-list li.exchange-row {
      flex-wrap: nowrap;
    }
    .card-details {
      flex-basis: 100%;
      width: 100%;
      margin-bottom: 0.25rem;
    }
    .exchange-row .card-details {
      flex-basis: auto;
      width: auto;
      margin-bottom: 0;
    }
    .list-quantity-controls {
      flex-shrink: 0;
    }
    .card-price {
      margin-left: auto;
    }
  }

  /* ========================================
     SWIPE-TO-ACTION
     ======================================== */
  .trade-group-list li.swipeable {
    display: block;
    padding: 0;
    background-color: transparent;
    position: relative;
    overflow: hidden;
  }

  .swipe-bg {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: none;
    align-items: center;
    z-index: 0;
  }

  .swipe-bg-left {
    justify-content: flex-end;
    padding-right: 16px;
    background-color: #ef4444;
  }

  .swipe-bg-right {
    justify-content: flex-start;
    padding-left: 16px;
    background-color: #22c55e;
  }

  .swipe-icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .swipe-icon svg {
    width: 22px;
    height: 22px;
    stroke: white;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
  }

  .swipe-content {
    position: relative;
    z-index: 1;
    background-color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-height: 44px;
    padding: 0.4rem 0.75rem;
    will-change: transform;
    border-bottom: 1px solid #f3f4f6;
  }

  /* ========================================
     EXCHANGE UI — finalized trade mode
     ======================================== */
  .exchange-header {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background-color: #f0fdf4;
    border-bottom: 1px solid #bbf7d0;
    position: sticky;
    top: 0;
    z-index: 2;
    gap: 0.5rem;
    cursor: pointer;
    user-select: none;
  }

  .exchange-header-readonly {
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }

  .exchange-header-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #374151;
  }

  .exchange-select-all-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #16a34a;
    flex: 1;
  }

  .exchange-header-price-spacer {
    width: 45px;
    flex-shrink: 0;
  }

  .exchange-progress-badge {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.15rem 0.5rem;
    border-radius: 9999px;
    background-color: #dcfce7;
    color: #16a34a;
    white-space: nowrap;
  }

  .exchange-row {
    transition:
      background-color 0.2s,
      opacity 0.2s;
    cursor: pointer;
  }

  .exchange-row.exchanged {
    background-color: #f0fdf4;
    opacity: 0.6;
  }

  .exchange-row.exchanged .card-name {
    text-decoration: line-through;
    color: #9ca3af;
  }

  .exchange-controls {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
    margin-right: 0.5rem;
  }

  .exchange-checkbox {
    accent-color: #22c55e;
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  .exchange-checkbox:disabled {
    cursor: default;
    opacity: 0.5;
  }

  .exchange-qty-btn {
    width: 24px;
    height: 24px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s;
    padding: 0;
  }

  .exchange-qty-btn:hover:not(:disabled) {
    background-color: #f3f4f6;
  }

  .exchange-qty-btn:disabled {
    opacity: 0.3;
    cursor: default;
  }

  .exchange-qty-display {
    font-size: 0.75rem;
    font-weight: 600;
    color: #374151;
    min-width: 28px;
    text-align: center;
  }

  /* ========================================
     ACCEPT FOOTER
     ======================================== */
  .accept-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem 1rem;
    border-top: 1px solid #e5e7eb;
    background-color: #f9fafb;
    flex-shrink: 0;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .sync-moxfield-btn {
    padding: 0.35rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 9999px;
    background: white;
    color: #374151;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s;
    font-family: inherit;
    white-space: nowrap;
  }

  .sync-moxfield-btn:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
</style>
